import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Globe, Smartphone } from 'lucide-react';
import { type ProjectItemBase } from '../data/portfolioData';
import { MobileDeviceMockup } from './MobileDeviceMockup';

interface ProjectsSectionProps {
  projects: ProjectItemBase[];
}

export const ProjectsSection: React.FC<ProjectsSectionProps> = ({ projects }) => {
  const { t } = useTranslation();

  const featuredProjects = projects.filter((p) => p.featured);
  const regularProjects = projects.filter((p) => !p.featured);

  return (
    <section className="py-16 border-b border-gray-800/80">
      <div className="mb-10">
        <span className="text-xs font-mono text-[#00F0FF] tracking-widest">{t('projects.badge')}</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-1">{t('projects.title')}</h2>
      </div>

      {/* 1. Featured Spotlight Project Showcase (Mobile App) */}
      {featuredProjects.map((project) => {
        const title = t(`projects.items.${project.id}.title`, { defaultValue: 'Executive Jiu-Jitsu — Mobile App' });
        const category = t(`projects.items.${project.id}.category`, { defaultValue: 'MOBILE ENGINEERING & ARCHITECTURE' });
        const description = t(`projects.items.${project.id}.description`, { defaultValue: '' });
        const metrics = t(`projects.items.${project.id}.metrics`, { defaultValue: '' });

        return (
          <div
            key={project.id}
            className="relative mb-12 rounded-2xl bg-gradient-to-br from-[#0B0F19] via-[#080B12] to-[#06080E] border border-gray-800 hover:border-[#00F0FF]/40 transition-all duration-300 p-6 sm:p-8 lg:p-10 overflow-hidden group shadow-2xl"
          >
            {/* Ambient Cyan Glows */}
            <div className="absolute top-0 right-0 w-[450px] h-[450px] bg-[#00F0FF]/[0.035] rounded-full blur-[130px] pointer-events-none" />
            <div className="absolute bottom-0 left-1/4 w-[350px] h-[350px] bg-[#00F0FF]/[0.02] rounded-full blur-[100px] pointer-events-none" />

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              
              {/* Left Column: Interactive Mobile Device Mockup */}
              <div className="lg:col-span-5 flex justify-center order-2 lg:order-1">
                <MobileDeviceMockup
                  mediaSrc={project.media?.src}
                  mediaType={project.media?.type}
                  poster={project.media?.poster}
                />
              </div>

              {/* Right Column: Technical Details & Actions */}
              <div className="lg:col-span-7 space-y-5 order-1 lg:order-2">
                {/* Meta Badge */}
                <div className="flex flex-wrap items-center gap-3 font-mono">
                  <span className="text-[#00F0FF] text-sm font-bold">{project.id}</span>
                  <span className="text-gray-600">//</span>
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 tracking-wider flex items-center space-x-1">
                    <Smartphone size={11} className="mr-1" />
                    {category}
                  </span>
                </div>

                {/* Title */}
                <div>
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight group-hover:text-[#00F0FF] transition-colors">
                    {title}
                  </h3>
                  <div className="text-xs font-mono text-gray-500 mt-1 flex items-center space-x-2">
                    <span>{t('projects.techFocusLabel')}</span>
                    <span className="text-[#00F0FF] font-semibold">{metrics}</span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed font-light font-sans">
                  {description}
                </p>

                {/* Tech Stack Pills */}
                <div className="space-y-2">
                  <div className="text-xs font-mono text-gray-500 tracking-wider">CORE TECH STACK:</div>
                  <div className="flex flex-wrap gap-2 font-mono text-xs">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="bg-[#0E1322] border border-gray-700/80 text-gray-200 px-3 py-1 rounded-md hover:border-[#00F0FF]/40 transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Buttons Bar: App Store, Google Play, Website */}
                <div className="pt-2 flex flex-wrap items-center gap-3">
                  {/* App Store Button */}
                  {project.links?.appStore && (
                    <a
                      href={project.links.appStore}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center space-x-2.5 px-4 py-2.5 rounded-lg bg-[#0E111B] border border-gray-700 text-white hover:border-[#00F0FF] hover:text-[#00F0FF] hover:shadow-lg hover:shadow-cyan-500/10 transition-all text-xs font-mono"
                    >
                      {/* Apple SVG Icon */}
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 170 170">
                        <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.04-7.58-7.7-11.64-13.98-5.74-8.8-10.22-19.14-13.43-30.01-3.21-10.87-4.82-21.43-4.82-31.69 0-14.36 3.63-26.27 10.88-35.73 7.26-9.46 16.51-14.28 27.76-14.47 4.9.11 10.3 1.34 16.2 3.69 5.9 2.35 9.77 3.64 11.6 3.86 1.49-.24 5.37-1.53 11.64-3.86 6.27-2.34 11.51-3.47 15.72-3.4 12.01.63 21.68 4.7 29.02 12.21-10.45 6.3-15.62 15.14-15.5 26.5.13 8.87 3.52 16.35 10.18 22.45 6.66 6.1 14.5 9.69 23.51 10.77-2.22 6.55-4.73 13.04-7.53 19.46zM119.22 33.24c0-7.39 2.66-14.19 7.97-20.4 5.31-6.21 11.83-10.15 19.56-11.84.21 1.05.32 2.05.32 3 0 7.39-2.77 14.43-8.3 21.12-5.54 6.69-12.08 10.46-19.63 11.31-.07-.98-.12-1.95-.12-3.19z" />
                      </svg>
                      <span>App Store</span>
                    </a>
                  )}

                  {/* Google Play Button */}
                  {project.links?.playStore && (
                    <a
                      href={project.links.playStore}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center space-x-2.5 px-4 py-2.5 rounded-lg bg-[#0E111B] border border-gray-700 text-white hover:border-[#00F0FF] hover:text-[#00F0FF] hover:shadow-lg hover:shadow-cyan-500/10 transition-all text-xs font-mono"
                    >
                      {/* Play Store SVG Icon */}
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 512 512">
                        <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
                      </svg>
                      <span>Google Play</span>
                    </a>
                  )}

                  {/* Website Link */}
                  {project.links?.website && (
                    <a
                      href={project.links.website}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-lg bg-transparent text-gray-300 hover:text-[#00F0FF] transition-colors text-xs font-mono group/link"
                    >
                      <Globe size={14} className="text-gray-400 group-hover/link:text-[#00F0FF]" />
                      <span>Ver Website</span>
                      <ArrowUpRight size={14} className="opacity-70 group-hover/link:opacity-100 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform text-[#00F0FF]" />
                    </a>
                  )}
                </div>

              </div>

            </div>
          </div>
        );
      })}

      {/* 2. Standard Tabular Projects Grid */}
      <div className="space-y-0 border-t border-gray-800">
        {regularProjects.map((project) => {
          const title = t(`projects.items.${project.id}.title`, { defaultValue: 'Project Title' });
          const category = t(`projects.items.${project.id}.category`, { defaultValue: 'Category' });
          const description = t(`projects.items.${project.id}.description`, { defaultValue: '' });
          const metrics = t(`projects.items.${project.id}.metrics`, { defaultValue: '' });

          return (
            <div
              key={project.id}
              className="group border-b border-gray-800 py-8 transition-all hover:bg-[#0A0D15]/50 px-2 sm:px-4"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">

                {/* Left Metadata */}
                <div className="lg:col-span-3 font-mono space-y-1">
                  <div className="flex items-center space-x-3">
                    <span className="text-[#00F0FF] text-sm font-bold">{project.id}</span>
                  </div>
                  <div className="text-xs text-gray-500 tracking-wider pt-1">{category}</div>
                </div>

                {/* Middle Details */}
                <div className="lg:col-span-6 space-y-3">
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block"
                  >
                    <h3 className="text-xl sm:text-2xl font-bold text-white group-hover:text-[#00F0FF] transition-colors flex items-center space-x-2">
                      <span>{title}</span>
                      <ArrowUpRight size={18} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#00F0FF]" />
                    </h3>
                  </a>

                  <p className="text-gray-400 text-sm leading-relaxed font-light font-sans">
                    {description}
                  </p>

                  <div className="flex flex-wrap gap-2 pt-1 font-mono text-[11px]">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="bg-[#0E111B] border border-gray-800 text-gray-300 px-2.5 py-0.5"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Right Impact Metric */}
                <div className="lg:col-span-3 lg:text-right font-mono">
                  <div className="text-xs text-gray-500 mb-1">{t('projects.techFocusLabel')}</div>
                  <div className="text-sm font-bold text-[#00F0FF] bg-[#00F0FF]/5 border border-[#00F0FF]/20 px-3 py-2 inline-block lg:block">
                    {metrics}
                  </div>
                </div>

              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
